import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { CACHE_VERSION } from "../lib/cacheVersion";

export interface SetterReplyTurnRow {
  setterId: string;
  setterName: string;
  totalCount: number;
  /** Mean reply turns across the setter's conversations. Window branch computes
   * the exact sum/count; lifetime branch leaves this 0 (inline card reads
   * metrics.avg_reply_turns there, not this hook). */
  avgTurns: number;
  buckets: Array<{ bucket: string; count: number; leadCount: number }>;
}

// Stale-while-revalidate cache. Live ranges (recent date) get short TTL so numbers
// stay reasonably fresh; historical ranges get long TTL since they don't change.
const CACHE_PREFIX = "altara_dm_reply_turn_v1";
const TTL_LIVE_MS = 15 * 60 * 1000; // 15 min
const TTL_HISTORICAL_MS = 24 * 60 * 60 * 1000; // 24h

function isLiveRange(endISO: string): boolean {
  // Live = end date is within last 48h (covers "today", "last 7d", current month)
  const end = new Date(endISO).getTime();
  return Date.now() - end < 48 * 60 * 60 * 1000;
}

function readCache(key: string): SetterReplyTurnRow[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      version: string;
      timestamp: number;
      ttl: number;
      data: SetterReplyTurnRow[];
    };
    if (parsed.version !== CACHE_VERSION) return null;
    if (Date.now() - parsed.timestamp > parsed.ttl) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: SetterReplyTurnRow[], ttl: number) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ version: CACHE_VERSION, timestamp: Date.now(), ttl, data })
    );
  } catch {
    // localStorage full or disabled — ignore
  }
}

// `label` matches BUCKET_ORDER.reply_turn_distribution in SetterProofModal.tsx
// — required so getBucketLabel + getBucketColor produce the right text/color.
const BUCKET_KEYS: Array<{ key: string; lead: string; label: string }> = [
  { key: "reply_turn_19plus", lead: "reply_turn_19plus_lead", label: "19plus" },
  { key: "reply_turn_13_18", lead: "reply_turn_13_18_lead", label: "13_18" },
  { key: "reply_turn_11_12", lead: "reply_turn_11_12_lead", label: "11_12" },
  { key: "reply_turn_10", lead: "reply_turn_10_lead", label: "10" },
  { key: "reply_turn_9", lead: "reply_turn_9_lead", label: "9" },
  { key: "reply_turn_8", lead: "reply_turn_8_lead", label: "8" },
  { key: "reply_turn_7", lead: "reply_turn_7_lead", label: "7" },
  { key: "reply_turn_6", lead: "reply_turn_6_lead", label: "6" },
  { key: "reply_turn_5", lead: "reply_turn_5_lead", label: "5" },
  { key: "reply_turn_4", lead: "reply_turn_4_lead", label: "4" },
  { key: "reply_turn_3", lead: "reply_turn_3_lead", label: "3" },
  { key: "reply_turn_2", lead: "reply_turn_2_lead", label: "2" },
  { key: "reply_turn_1", lead: "reply_turn_1_lead", label: "1" },
  { key: "reply_turn_0", lead: "reply_turn_0_lead", label: "0" },
];

// Mirrors the CASE statement in sql/get_setter_proof_rows.sql:501-516 byte-for-byte.
// Used by the window-mode branch to bucket in_window_reply_turns client-side.
function bucketOf(n: number): string {
  if (n === 0) return "0";
  if (n === 1) return "1";
  if (n === 2) return "2";
  if (n === 3) return "3";
  if (n === 4) return "4";
  if (n === 5) return "5";
  if (n === 6) return "6";
  if (n === 7) return "7";
  if (n === 8) return "8";
  if (n === 9) return "9";
  if (n === 10) return "10";
  if (n >= 11 && n <= 12) return "11_12";
  if (n >= 13 && n <= 18) return "13_18";
  return "19plus";
}

interface Args {
  organizationId: string | null;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  windowMode: "lifetime" | "window";
}

export function useReplyTurnDistributionAll({
  organizationId,
  setterIds,
  dateRange,
  windowMode,
}: Args) {
  const [rows, setRows] = useState<SetterReplyTurnRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startISO = dateRange.start?.toISOString() || null;
  const endISO = dateRange.end?.toISOString() || null;
  const setterIdsKey = setterIds.join(",");

  useEffect(() => {
    if (!organizationId || !startISO || !endISO || setterIds.length === 0 || !supabase) {
      setRows([]);
      return;
    }
    const sb = supabase;
    // Cache key includes windowMode so lifetime and window entries never collide.
    const cacheKey = `${CACHE_PREFIX}|${organizationId}|${startISO}|${endISO}|${setterIdsKey}|${windowMode}`;
    const ttl = isLiveRange(endISO) ? TTL_LIVE_MS : TTL_HISTORICAL_MS;

    let cancelled = false;
    setError(null);

    // Stale-while-revalidate: hydrate from cache instantly, then refetch in background.
    const cached = readCache(cacheKey);
    if (cached) {
      setRows(cached);
      setLoading(false); // chart renders immediately
    } else {
      setLoading(true); // cold cache — show skeleton
    }

    (async () => {
      try {
        const endDate = new Date(endISO);
        endDate.setHours(23, 59, 59, 999);

        let built: SetterReplyTurnRow[];

        if (windowMode === "lifetime") {
          // ── LIFETIME MODE ──────────────────────────────────────────────────────
          // Existing RPC path — unchanged.
          const [rpcResult, namesResult] = await Promise.all([
            sb.rpc("get_reply_turn_distribution", {
              p_organization_id: organizationId,
              p_setter_ids: setterIds,
              p_start_date: startISO,
              p_end_date: endDate.toISOString(),
            }),
            sb.from("profiles").select("ghl_user_id, full_name").in("ghl_user_id", setterIds),
          ]);

          if (cancelled) return;
          if (rpcResult.error) throw rpcResult.error;

          const nameMap = new Map<string, string>();
          for (const p of (namesResult.data || []) as Array<{
            ghl_user_id: string | null;
            full_name: string | null;
          }>) {
            if (p.ghl_user_id) nameMap.set(p.ghl_user_id, p.full_name || "Unknown");
          }

          built = (rpcResult.data || []).map((r: Record<string, unknown>) => {
            const setterId = String(r.setter_id || "");
            const buckets = BUCKET_KEYS.map((bk) => ({
              bucket: bk.label,
              count: Number(r[bk.key] || 0),
              leadCount: Number(r[bk.lead] || 0),
            }));
            const totalCount = Number(r.total_conversations || 0);
            return {
              setterId,
              setterName: nameMap.get(setterId) || "Unknown",
              totalCount,
              avgTurns: 0, // lifetime inline card uses metrics.avg_reply_turns, not this
              buckets,
            };
          });
        } else {
          // ── WINDOW MODE ────────────────────────────────────────────────────────
          // New RPC: returns one flat row per (setter, conversation).
          // BIGINT columns can arrive as string from postgrest — Number() cast is defensive.
          const [rpcResult, namesResult] = await Promise.all([
            sb.rpc("get_setter_per_lead_reply_turns_window", {
              p_organization_id: organizationId,
              p_setter_ids: setterIds,
              p_start_date: startISO,
              p_end_date: endDate.toISOString(),
            }),
            sb.from("profiles").select("ghl_user_id, full_name").in("ghl_user_id", setterIds),
          ]);

          if (cancelled) return;
          if (rpcResult.error) throw rpcResult.error;

          const nameMap = new Map<string, string>();
          for (const p of (namesResult.data || []) as Array<{
            ghl_user_id: string | null;
            full_name: string | null;
          }>) {
            if (p.ghl_user_id) nameMap.set(p.ghl_user_id, p.full_name || "Unknown");
          }

          // Group flat rows by setter_id, then bucket each row's in_window_reply_turns.
          type WindowRow = {
            setter_id: string;
            conversation_id: string;
            in_window_reply_turns: number | string;
            last_message_from: string | null;
          };

          const grouped = new Map<string, WindowRow[]>();
          for (const r of (rpcResult.data || []) as WindowRow[]) {
            const sid = String(r.setter_id || "");
            if (!grouped.has(sid)) grouped.set(sid, []);
            grouped.get(sid)!.push(r);
          }

          built = Array.from(grouped.entries()).map(([setterId, convRows]) => {
            // Tally count (conversations in bucket) and leadCount (where last_message_from === 'lead').
            const bucketCounts = new Map<string, number>();
            const bucketLeadCounts = new Map<string, number>();
            let sumTurns = 0; // exact in-window turn total → drives the inline card's avg

            for (const row of convRows) {
              const turns = Number(row.in_window_reply_turns ?? 0);
              sumTurns += turns;
              const bkt = bucketOf(turns);
              bucketCounts.set(bkt, (bucketCounts.get(bkt) ?? 0) + 1);
              if (row.last_message_from === "lead") {
                bucketLeadCounts.set(bkt, (bucketLeadCounts.get(bkt) ?? 0) + 1);
              }
            }

            const buckets = BUCKET_KEYS.map((bk) => ({
              bucket: bk.label,
              count: bucketCounts.get(bk.label) ?? 0,
              leadCount: bucketLeadCounts.get(bk.label) ?? 0,
            }));

            return {
              setterId,
              setterName: nameMap.get(setterId) || "Unknown",
              totalCount: convRows.length,
              avgTurns: convRows.length ? sumTurns / convRows.length : 0,
              buckets,
            };
          });
        }

        if (cancelled) return;

        built.sort((a, b) => b.totalCount - a.totalCount);
        const filtered = built.filter((r) => r.totalCount > 0);
        setRows(filtered);
        writeCache(cacheKey, filtered, ttl);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId, startISO, endISO, setterIdsKey, windowMode]); // eslint-disable-line react-hooks/exhaustive-deps
  // setterIds excluded from deps in favor of setterIdsKey (stable string) to avoid re-fetch on array identity change

  return { rows, loading, error };
}
